import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import AccountTable from '../../shared/components/AccountManagement/AccountTable'
import AccountEdit from '../../shared/components/AccountManagement/AccountEdit'

import { Grid } from '@mui/material'

import DropDownComponent, {
  DropDownElement
} from '@professocial/professocialFormComponents/FormComponents/DropDownComponent'
import { HiddenAttr, TableTemplate, Template } from '../Admin/AdminAccounts'
import { useDispatch, useSelector } from 'react-redux'
import {
  CreateUserAccountRequest,
  DeleteUserAccountRequest,
  GetAllUsersRequest,
  UpdateUserAccountRequest
} from '@professocialApi/professocial-core-frontend-api/apis/UserLoginRESTServiceApi'
import {
  createUserAccountInitiated,
  deleteUserAccountInitiated, forceDisconnectInitiated,
  getAllUsersInitiated,
  listSubsidiariesInitiated,
  setOnSuccess,
  updateUserAccountInitiated,
} from '../../store/accounts/accounts.actions'
import {
  ResultPageWebModel,
  Subsidiary,
  UserLoginSortOrder,
  UserLoginSortValue,
  UserSettings
} from '@professocialApi/professocial-core-frontend-api'
import { getAllSubsidiaries, getAllUserAccounts } from '../../store/accounts/accounts.selector'
import { SaveSuccess } from '../../store/accounts/accounts.reducer'

import { ListSubsidiariesRequest } from '@professocialApi/professocial-core-frontend-api/apis/SubsidiaryRESTServiceApi'

import DialogComponent from '@professocial/professocialFormComponents/FormComponents/DialogComponent'
import { Route, Switch, useHistory, useLocation, useRouteMatch } from 'react-router'
import { BASE_PATH } from '../../config/envConfig'
import { ActionCall } from '@professocial/professocialTypes/types'
import { RootState } from '../../store/accountRoot.reducer'
import { namedRequestData } from '../../store/common/common.selectors'
import { clearRequests } from '../../store/common/common.actions'

export type FilterParams = {
  limit: number
  offset: number
  sortOrder: UserLoginSortOrder
  sortValue?: UserLoginSortValue | undefined
  searchWith?: string
}

interface IProps {
  user?: UserSettings;
}

const EditorAccounts: React.FC<IProps> = (props) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { path } = useRouteMatch()
  const location = useLocation()
  const history = useHistory()

  const xsrfToken = sessionStorage.getItem('XSRF-TOKEN')
  const requestHeaders: HeadersInit = new Headers()
  requestHeaders.set('Accept', 'application/json')
  requestHeaders.set('Content-Type', 'application/json')
  if (xsrfToken) requestHeaders.set('X-XSRF-TOKEN', xsrfToken)

  const dispatchGetAllUsers = useCallback((request: GetAllUsersRequest) => dispatch(getAllUsersInitiated(request)), [dispatch])
  const dispatchListSubsidiaries = useCallback((request: ListSubsidiariesRequest) => dispatch(listSubsidiariesInitiated(request)), [dispatch])
  const dispatchCreateUserAccount = useCallback((request: CreateUserAccountRequest) => dispatch(createUserAccountInitiated(request)), [dispatch])
  const dispatchDeleteUserAccount = useCallback((request: DeleteUserAccountRequest) => dispatch(deleteUserAccountInitiated(request)), [dispatch])
  const dispatchUpdateUserAccount = useCallback((request: UpdateUserAccountRequest) => dispatch(updateUserAccountInitiated(request)), [dispatch])
  const dispatchSaveSuccess = useCallback((status: SaveSuccess | null) => dispatch(setOnSuccess(status)), [dispatch])
  const dispatchClearRequests = useCallback(() => dispatch(clearRequests()), [dispatch])

  const allUserAccounts: ResultPageWebModel | undefined = useSelector(getAllUserAccounts)
  const allSubsidiaries: Subsidiary[] | undefined = useSelector(getAllSubsidiaries)

  const actionOnUserCreate: ActionCall | undefined = useSelector((state: RootState) => namedRequestData(state, createUserAccountInitiated.type))
  const actionOnUserUpdate: ActionCall | undefined = useSelector((state: RootState) => namedRequestData(state, updateUserAccountInitiated.type))
  const actionOnUserDelete: ActionCall | undefined = useSelector((state: RootState) => namedRequestData(state, deleteUserAccountInitiated.type))
  const actionOnForceDisconnect = useSelector((state: RootState) => namedRequestData(state, forceDisconnectInitiated.type))

  useEffect(() => {
    if (actionOnUserCreate?.status === 'success') {
      history.goBack()
      loadData()
    }

    if (actionOnUserCreate?.status === 'failed' && actionOnUserCreate?.error) {
      setToast({
        autoClose: false,
        open: true,
        type: 'error',
        title: t('topicsMessages:error'),
        message: t('accountsCommon:cannotCreateUser'),
        severity: 'error',
        requestActions: true,
        cancelLabel: null,
        confirmLabel: t('topicsCommon:ok'),
        handleClose: () => setToast(null),
        handleConfirm: () => {
          setToast(null)
          dispatchClearRequests()
        },
      })
    }

    if (actionOnUserUpdate?.status === 'success') {
      history.goBack()
      loadData()
    }

    if (actionOnUserDelete?.status === 'success') {
      loadData()
    }

    if (actionOnForceDisconnect?.status === 'success') {
      loadData()
    }
  }, [actionOnUserUpdate, actionOnUserCreate, actionOnUserDelete, actionOnForceDisconnect])

  const defaultDateFormat = 'dd.MM.yyyy'
  const defaultTimeFormat = 'HH:mm'
  const tableTemplate: TableTemplate = {
    user: [
      {
        name: 'username',
        hidden: undefined,
        sortable: UserLoginSortValue.Username,
        label: 'username',
      },
      {
        name: 'email',
        hidden: HiddenAttr.smUp,
        sortable: UserLoginSortValue.Email,
        label: 'email',
      },
      {
        name: 'lastName, firstName',
        hidden: HiddenAttr.mdUp,
        sortable: UserLoginSortValue.LastName,
        label: 'name',
      },
      {
        name: 'createdAt',
        hidden: HiddenAttr.mdUp,
        sortable: UserLoginSortValue.CreatedAt,
        label: 'createdAt',
        format: `${defaultDateFormat}, ${defaultTimeFormat}`,
      },
      {
        name: 'lastLogin',
        hidden: HiddenAttr.lgUp,
        sortable: UserLoginSortValue.LastLogin,
        label: 'lastLogin',
        format: `${defaultDateFormat}, ${defaultTimeFormat}`,
      },
      {
        name: 'mandator.label',
        hidden: HiddenAttr.lgUp,
        sortable: UserLoginSortValue.Mandator,
        label: 'mandator',
      },
      {
        name: 'subsidiary.label',
        hidden: HiddenAttr.lgUp,
        label: 'subsidiary',
      }],
    detainee: [
      {
        name: 'username',
        hidden: undefined,
        sortable: UserLoginSortValue.Username,
        label: 'username',
      },
      {
        name: 'email',
        hidden: HiddenAttr.smUp,
        sortable: UserLoginSortValue.Email,
        label: 'email',
      },
      {
        name: 'lastName, firstName',
        hidden: HiddenAttr.mdUp,
        sortable: UserLoginSortValue.LastName,
        label: 'name',
      },
      {
        name: 'createdAt',
        hidden: HiddenAttr.mdUp,
        sortable: UserLoginSortValue.CreatedAt,
        label: 'createdAt',
        format: `${defaultDateFormat}, ${defaultTimeFormat}`,
      },
      {
        name: 'lastLogin',
        hidden: HiddenAttr.lgUp,
        sortable: UserLoginSortValue.LastLogin,
        label: 'lastLogin',
        format: `${defaultDateFormat}, ${defaultTimeFormat}`,
      },
      {
        name: 'mandator.label',
        hidden: HiddenAttr.lgUp,
        sortable: UserLoginSortValue.Mandator,
        label: 'mandator',
      },
    ],
  }

  const [data, setData] = React.useState<ResultPageWebModel>()
  const [subsidiaries, setSubsidiaries] = useState<Subsidiary[]>([])
  const [toast, setToast] = React.useState<any>()
  const [role, setRole] = React.useState('user')
  const [accountQueryParams, setAccountQueryParams] = React.useState<FilterParams>({
    limit: 25,
    offset: 0,
    sortOrder: UserLoginSortOrder.Desc,
    sortValue: undefined,
    searchWith: '',
  })

  useEffect(() => {
    loadSubsidiaryData()
  }, [],
  )

  useEffect(() => {
    setData(undefined)
  }, [role],
  )

  useEffect(() => {
    loadData()
  }, [accountQueryParams, role],
  )

  const loadSubsidiaryData = () => {
    if (props.user && props.user.mandator?.id) {
      const request: ListSubsidiariesRequest = {
        mandatorId: props.user.mandator.id,
      }
      dispatchListSubsidiaries(request)
    }
  }

  useEffect(() => {
    if (allSubsidiaries) {
      setSubsidiaries(allSubsidiaries)
    }
  }, [allSubsidiaries])

  useEffect(() => {
    if (allUserAccounts) {
      setData(allUserAccounts)
    }
  }, [allUserAccounts])

  useEffect(() => {
    const pathname = location.pathname
    const path = pathname.split('/')
    const key = filterOptions.filter(item => item.key === path[3])
    key && key[0] && setRole(key[0].key)
  }, [location])

  const handleMessage = (data: any) => {
    setToast(data)
  }

  const loadData = () => {
    if (role) {
      const requestParams: GetAllUsersRequest = {
        role: role,
        limit: accountQueryParams.limit,
        offset: accountQueryParams.offset,
        searchWith: accountQueryParams.searchWith,
      }
      if (accountQueryParams.sortValue) {
        requestParams.sortValue = accountQueryParams.sortValue
        requestParams.sortOrder = accountQueryParams.sortOrder
      }
      dispatchGetAllUsers(requestParams)
    }
    dispatchSaveSuccess(null)
  }

  const reloadData = (newParams: FilterParams) => {
    if (JSON.stringify(accountQueryParams) !== JSON.stringify(newParams)) {
      setAccountQueryParams(newParams)
    }
  }

  const deleteUserQuestion = (userId: string) => {
    setToast({
      autoClose: false,
      open: true,
      type: 'warning',
      title: t('networkContentMessages:deleteWarningTitle'),
      message: t('accountsCommon:userDeletingQuestion'),
      severity: 'error',
      requestActions: true,
      confirmLabel: t('networkContentCommon:delete'),
      cancelLabel: t('networkContentCommon:cancel'),
      handleClose: () => setToast(null),
      handleCancel: () => setToast(null),
      handleConfirm: () => {
        setToast(null)
        deleteUser(userId)
      },
    })
  }

  const deleteUser = (userId: string) => {
    const deleteRequest: DeleteUserAccountRequest = {
      userId: userId,
    }
    dispatchDeleteUserAccount(deleteRequest)
  }

  const createNewUser = (data: any) => {
    const createRequest: CreateUserAccountRequest = {
      userLogin: data,
    }
    dispatchCreateUserAccount(createRequest)
  }

  const resetFailedLoginCount = (data: any) => {
    const updateRequest: UpdateUserAccountRequest = {
      userId: data.id,
      userLogin: data,
    }
    dispatchUpdateUserAccount(updateRequest)
  }

  const changeUserData = (data: any) => {
    const updateRequest: UpdateUserAccountRequest = {
      userId: data.id,
      userLogin: data,
    }
    dispatchUpdateUserAccount(updateRequest)
  }

  const filterOptions: DropDownElement[] = [
    {
      key: 'user',
      value: t('accountsCommon:user'),
    },
    {
      key: 'detainee',
      value: t('accountsCommon:detainee'),
    },
  ]

  const csvExport = () => {
    if (props.user?.role === 'ADMIN') {
      if (role === 'detainee') {
        window.open(
          `${BASE_PATH}/rest/professocial/core/gatekeeper/statistic/current/mandator/detainees`,
          'csv',
        )
      } else {
        window.open(
          `${BASE_PATH}/rest/professocial/core/gatekeeper/statistic/current/mandator/users`,
          'csv',
        )
      }
    }
  }
  return (
    <Grid container style={{ margin: '25px 0px' }}>
      <Grid xs={12} item container direction="row">
        <Grid container item xs={12}>
          <Switch>
            <Route exact path="/editor/accounts">
              <>
                <Grid xs={3} item container sx={{ mb: 3 }} direction="row">
                  <DropDownComponent
                    options={filterOptions}
                    value={role}
                    label={t('accountsCommon:filter')}
                    size="small"
                    disableClearable
                    onChange={(option: DropDownElement) => {
                      setRole(option.key)
                    }}
                  />
                </Grid>
                <AccountTable
                  data={data}
                  pageKey={role}
                  filterParams={accountQueryParams}
                  mandatorOptions={props.user?.mandator}
                  template={(role && tableTemplate[role] ? tableTemplate[role] : tableTemplate.user) as Template[]}
                  user={props.user}
                  csvExport={() => csvExport()}
                  deleteUser={(userId: string) => deleteUserQuestion(userId)}
                  setFilterSettings={reloadData}
                />
              </>
            </Route>
            <Route path={`${path}/edit/:id`}>
              <AccountEdit
                user={props.user}
                subsidiaries={subsidiaries}
                role="EDITOR"
                data={data}
                onEdit
                handleMessage={handleMessage}
                changeUserData={(data: any) => changeUserData(data)}
                resetFailedLoginCount={(data: any) => resetFailedLoginCount(data)}
              />
            </Route>
            <Route path={`${path}/add_user`}>
              <AccountEdit
                user={props.user}
                subsidiaries={subsidiaries}
                role="EDITOR"
                createNewUser={(data: any) => createNewUser(data)}
              />
            </Route>
          </Switch>
          {toast &&(
            <DialogComponent
              type={toast.type || undefined}
              autoClose={toast.autoClose}
              title={toast.title}
              message={toast.message}
              open={toast.open}
              confirmLabel={toast.confirmLabel}
              cancelLabel={toast.cancelLabel}
              requestActions={toast.requestActions}
              onClose={toast.handleClose}
              onCancel={toast.handleCancel || undefined}
              onConfirm={toast.handleConfirm || undefined}
            />
          )}
        </Grid>
      </Grid>
    </Grid>
  )
}
export default EditorAccounts
